import { debounce, throttle } from "./timing";

/**
 * Gathers a list of focusable elements within the passed in element (including the element itself).
 * Thank you to https://zellwk.com/blog/keyboard-focusable-elements/ for the inspiration
 * @param {Element} element
 * @returns An array comprised of focusable elements
 */
export function getFocusableElements(element) {
    if (!element) {
        console.error("getFocusableElements: element does not exist");
        return [];
    }
    const focusableElementsSelector = 'a[href], button, input:not([type=hidden]), textarea, select, details, [tabindex], [contenteditable]';
    const focusableElements = [];

    // if the container element itself is focusable, add it to the list first
    if (element.matches(focusableElementsSelector)) {
        focusableElements.push(element);
    }
    focusableElements.push(...element.querySelectorAll(focusableElementsSelector));

    // filter by relevant tabindex, styling, and disabled attributes
    return focusableElements.filter((element) => {
        if (element.hasAttribute('tabindex') && (element.getAttribute('tabindex') < 0)) {
            return false;
        }
        if (element.hasAttribute('disabled') || element.hasAttribute('hidden')) {
            return false;
        }
        if (getComputedStyle(element).display === 'none' || getComputedStyle(element).visibility === 'hidden') {
            return false;
        }
        return true;
    });
}

/**
 * Breakpoints
 */
export const desktopBreakpoint = 991;

/**
 * Checks if the viewport is in desktop width
 * @returns {Boolean}
 */
export function isDesktop() {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  return (viewportWidth > desktopBreakpoint);
}

/**
 * Dispatches a breakpoint event to the window to be used globally
 */
export class BreakpointTracker {

    constructor() {
        this.breakpoints = {
            small: '(max-width: 574.98px)',
            medium: '(min-width: 575px) and (max-width: 767.98px)',
            large: '(min-width: 768px) and (max-width: 991.98px)',
            xlarge: '(min-width: 992px) and (max-width: 1199.98px)',
            xxlarge: '(min-width: 1200px)',
        }
        this.currentBreakpoint = null
    }

    init() {
        for (const breakpoint in this.breakpoints) {
            const mediaQuery = window.matchMedia(this.breakpoints[breakpoint])
            mediaQuery.addEventListener('change', () => this.checkBreakpoint())
        }
        this.checkBreakpoint()
    }

    checkBreakpoint() {
        for (const breakpoint in this.breakpoints) {
            if (window.matchMedia(this.breakpoints[breakpoint]).matches) {
                if (this.currentBreakpoint !== breakpoint) {
                    this.breakpointChangeEvent(breakpoint)
                }
                break
            }
        }
    }

    breakpointChangeEvent(breakpoint) {
        const breakpointChangeEvent = new CustomEvent('breakpointChange', {
            detail: {breakpoint, screenWidth: this.getScreenWidth(), isDesktop: isDesktop()}
        })
        window.dispatchEvent(breakpointChangeEvent)
    }

    getScreenWidth() {
        return window.innerWidth || document.documentElement.clientWidth
    }
}

/**
 * Sets up a sticky element using a clone method. This solution can be improved a lot, but
 * for now, all sticky functionality should be able to be managed with this function and
 * a chunk of CSS.
 * @param {Element} stickElement Element that be cloned and stuck to the DOM
 * @param {Number} triggerHeight Offsets the sticky functionality to take place at the given scroll offset from the top (defaults to 0)
 * @param {String} stickyClass (defaults to 'sticky')
 * @param {String} hiddenClass (defaults to 'hidden')
 * @param {Boolean} mobileActive Dictates if the element is sticky on mobile (defaults to true)
 * @param {Boolean} desktopActive Dictates if the element is sticky on desktop (defaults to true)
 */
export function setStickyElement(stickElement, triggerHeight = 0, stickyClass = 'sticky', hiddenClass = 'hidden', mobileActive = true, desktopActive = true) {
    var stickElementTopPosition = stickElement.getBoundingClientRect().top + window.scrollY;
    var stickElementClone = stickElement.cloneNode(true);
    stickElementClone.classList.add(stickyClass);
    var stickElementParentNode = stickElement.parentNode;

    window.addEventListener('scroll', throttle(() => evalutateSticky(), 100));
    window.addEventListener('resize', debounce(() => evalutateSticky(), 300))

    function evalutateSticky() {
        // if desktop & desktop isn't active - or - if not desktop and not mobile active
        if ((isDesktop() && !desktopActive) || (!isDesktop() && !mobileActive)) {
            // remove the sticky status and do nothing
            removeStickyStatus();
            return;
        }

        // if the scroll value has passed our threshold, make sticky
        if (window.scrollY > (stickElementTopPosition + triggerHeight)) {
            addStickyStatus()
        } else {
            removeStickyStatus()
        }
    }

    function addStickyStatus() {
        stickElement.classList.add(hiddenClass);
        if (!stickElementParentNode.contains(stickElementClone)) {
            stickElementParentNode.appendChild(stickElementClone);
        }
    }

    function removeStickyStatus() {
        stickElement.classList.remove(hiddenClass);
        if (stickElementParentNode.contains(stickElementClone)) {
            stickElementParentNode.removeChild(stickElementClone);
        }
    }
}

/**
 * Searches backwards in the DOM amongst siblings (similar to prev() in jquery)
 * @param {Element} searchFromElement the element that the search starts at
 * @param {String} searchSelector the selector to search for
 * @returns {Element} the found element or null
 */
export function prevMatch(searchFromElement, searchSelector) {
    let prevElement = searchFromElement.previousElementSibling;

    while(!prevElement.matches(searchSelector) && prevElement) {
        prevElement = prevElement.previousElementSibling;
    }

    return prevElement;
}

/**
 * Returns true if there is vertical overflow
 * - ***NOTE***: This will only work if the overflow on the element is not set to "visible"
 * @param {Element} el - element to test overflow measurements on
 * @returns {Boolean}
 */
export function isOverflowingY(el) {
    return (el.scrollHeight > el.offsetHeight);
}

/**
 * ### Allows us to see if element can be focused programmatically
 * - Credit to https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/examples/dialog/ for the idea/code snippets
 */
export function attemptFocus(element) {
  try {
    element.focus();
  } catch (e) {
    // continue regardless of error
  }
  return document.activeElement === element;
}

/**
 * ### Recursive function to set focus on descendant nodes until the first focusable element is found.
 * - Credit to https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/examples/dialog/ for the idea/code snippets
 * @returns {Boolean} true if a focusable element is found and focus is set.
 */
export function focusFirstDescendant(element) {
  // check if element itself is focusable
  if (attemptFocus(element)) {
    return true;
  }
  // if the element has children and element is not focusable
  if (element.childNodes) {
    for (var i = 0; i < element.childNodes.length; i++) {
      var child = element.childNodes[i];
      if (attemptFocus(child) || focusFirstDescendant(child)) {
        // attempt to find a child that is focusable
        return true;
      }
    }
  }

  return false;
}

/**
 * ### Recursive function to set focus on descendant nodes until the first focusable element is found (starting from the end).
 * - Credit to https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/examples/dialog/ for the code
 * @returns {Boolean} true if a focusable element is found and focus is set.
 */
export function focusLastDescendant(element) {
  // if the element has children and element is not focusable
  if (element.childNodes) {
    for (var i = element.childNodes.length - 1; i >= 0; i--) {
      var child = element.childNodes[i];
      if (attemptFocus(child) || focusLastDescendant(child)) {
        // attempt to find a child that is focusable
        return true;
      }
    }
  }
  // check if element itself is focusable
  if (attemptFocus(element)) {
    return true;
  }

  return false;
}

/**
 * Freeze and Unfreeze site scroll functions
 * To be called in specific modal open/close event chains so the user is unable to scroll the background site when the given modal is opened
 */

// Freeze Scroll
export const freezeScroll = () => {
    const html = document.documentElement;
    const scrollDepth = Math.round(window.scrollY);

    html.dataset.scrollDepth = scrollDepth

    html.classList.add('freeze-scroll');

    html.style.position = 'fixed';
    html.style.top = `-${scrollDepth}px`;
    html.style.width = '100%';
}

// Unfreeze Scroll
export const unfreezeScroll = () => {
    const html = document.documentElement;
    if (!html.classList.contains('freeze-scroll')) return;

    const scrollDepth = parseInt(html.dataset.scrollDepth || "0", 10);

    html.classList.remove('freeze-scroll');

    html.style.position = '';
    html.style.top = '';
    html.style.width = ''

    requestAnimationFrame(() => {
        window.scrollTo(0, scrollDepth);
    })
}

/**
 * OneTrust Freeze Scroll
 * Custom freeze scroll functionality to assure window scroll is frozen and no layout shift occurs when OT prefence center is opened/closed
 */

// Custom modal settings object
const otModalSettings = {
    openTimeout: null,
    closeTimeout: null,
    lastModalState: null
};

// Create mutation observer to watch when OT modal attributes change
const otObserver = new MutationObserver(() => {
    requestAnimationFrame(() => {
        const modal = document.getElementById('onetrust-pc-sdk')
        if (!modal) return
        if (modal.classList.contains('ot-hide')) return

        const modalVisible = getComputedStyle(modal).display !== 'none'

        if (modalVisible && otModalSettings.lastModalState !== 'open') {
            otModalSettings.lastModalState = 'open'
            clearTimeout(otModalSettings.openTimeout)
            otModalSettings.openTimeout = setTimeout(() => {
                document.body.style.overflow = ''
                freezeScroll()
            }, 450)
        } else if (!modalVisible && otModalSettings.lastModalState !== 'closed') {
            otModalSettings.lastModalState = 'closed'
            clearTimeout(otModalSettings.closeTimeout)
            otModalSettings.closeTimeout = setTimeout(() => {
                unfreezeScroll()
            }, 450)
        }
    })
})

// Use interval timer to check when OT modal is loaded
const checkOTModal = setInterval(() => {
    const modal = document.getElementById('onetrust-pc-sdk')
    if (modal) {
        otObserver.observe(modal, { attributes: true, attributeFilter: ['style'] }) // add observer to modal
        clearInterval(checkOTModal) // clear interval when modal loaded
    }
}, 500)
